import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Download, Clock } from "lucide-react";
import { setPageMeta } from "@/lib/seo";
import { trackEvent } from "@/lib/analytics";

// Autrice del PDF "Le 10 regole d'oro per accompagnare tuo figlio alla primaria"
const EDUCATRICE = {
  nome: "Valeria Vergani",
  qualifica: "Insegnante nella scuola dell'infanzia dal 1991",
  // bio come array di segmenti per gestire il grassetto in render
  bioParts: [
    { t: "Valeria lavora come insegnante nella scuola dell'infanzia dal 1991. Da " },
    { t: "oltre dieci anni ricopre anche il ruolo di coordinatrice di una scuola dell'infanzia all'interno di un Istituto Comprensivo a nord di Milano", bold: true },
    { t: ". La sua esperienza professionale è incentrata sulla progettazione educativa rivolta ai bambini " },
    { t: "dai 3 ai 6 anni", bold: true },
    { t: ", sull'osservazione e sul sostegno ai loro percorsi di crescita e sviluppo. È inoltre parte di un " },
    { t: "Coordinamento Pedagogico dei Comuni dell'area a nord di Milano", bold: true },
    { t: ", contribuendo al confronto e alla condivisione di pratiche e progettualità educative per la prima infanzia sul territorio." },
  ],
  photoUrl: "/educatrice/valeria-vergani.jpg",
};

export default function GuidaSceltaScuola() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  useEffect(() => {
    setPageMeta({
      title:
        "Pronti alla primaria? Senza fretta — Guida gratuita | Per manina",
      description:
        "Ricevi in regalo la guida 'Pronti alla primaria? Senza fretta': 10 regole d'oro per accompagnare i bambini dell'ultimo anno di scuola dell'infanzia. Racconta la tua esperienza e scaricala subito.",
      canonicalPath: "/guida-scelta-scuola",
      ogType: "website",
    });
    trackEvent("landing_view", { landing: "guida-scelta-scuola" });
  }, []);

  const submitSearch = (e) => {
    e?.preventDefault();
    const q = query.trim();
    trackEvent("landing_search_submit", {
      landing: "guida-scelta-scuola",
      query_len: q.length,
    });
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    params.set("ref", "guida");
    navigate(`/cerca?${params.toString()}`);
  };

  return (
    <div className="bg-[#FAF9F6]" data-testid="guida-scelta-scuola-page">
      {/* ================ HERO ================ */}
      <section className="relative overflow-hidden bg-[#1A362D]">
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
            {/* Copy */}
            <div>
              <p className="label-eyebrow text-[#F2C94C] tracking-wider">
                GUIDA GRATUITA · 7 PAGINE · PDF
              </p>
              <h1
                className="mt-4 font-ui font-black text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight relative inline-block px-10 py-4"
                data-testid="guida-hero-title"
              >
                {/* Pennellata/macchia cream dietro al titolo — SVG organic blob */}
                <svg
                  aria-hidden="true"
                  viewBox="0 0 540 220"
                  preserveAspectRatio="none"
                  className="absolute inset-0 w-full h-full pointer-events-none"
                >
                  <path
                    d="M22,48 C4,78 0,122 22,156 C40,190 90,210 160,210 C260,214 360,214 440,200 C502,190 532,162 534,118 C538,66 512,24 448,14 C360,-2 240,0 160,10 C90,20 42,22 22,48 Z"
                    fill="#FBF8F3"
                  />
                </svg>
                <span className="relative text-[#C05C45]">La tua esperienza</span>
                <br />
                <span className="relative text-[#1A362D]">vale </span>
                <span className="relative text-[#F2C94C]">oro.</span>
              </h1>
              <p className="mt-6 font-ui text-base sm:text-lg text-white/90 leading-relaxed max-w-xl">
                Compila la scheda di commento alla tua scuola dell'infanzia e scarichi subito il PDF{" "}
                <em className="text-[#F2C94C] not-italic font-semibold">
                  "Le 10 regole d'oro per accompagnare tuo figlio alla primaria"
                </em>
                . Scritto in collaborazione con Valeria Vergani, una professionista in ambito educativo con 35 anni di esperienza.{" "}
                <span className="text-[#F2C94C] font-semibold">
                  Gratis, immediato, senza email di mezzo.
                </span>
              </p>

              <p className="mt-4 font-ui text-[15px] text-white/85 leading-relaxed max-w-xl">
                Su Per manina ci sono oltre 21.000 scuole dell'infanzia italiane già indicizzate. Cerca la tua, apri la scheda e lascia il tuo contributo. Bastano due minuti: un'emoji per gli aspetti che ti stanno più a cuore, un sì/no sulla scelta, e se vuoi anche una frase libera.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <Link
                  to="/cerca"
                  data-testid="guida-hero-cta-primary"
                  className="btn-primary text-base px-6 py-3 whitespace-nowrap"
                >
                  Commenta la tua scuola e ottieni la guida
                </Link>
                <p className="text-xs text-white/60 font-ui">
                  2 minuti · Nessuna registrazione lunga · PDF immediato
                </p>
              </div>
            </div>

            {/* Mockup copertina PDF */}
            <div className="hidden lg:flex justify-center">
              <div className="relative w-72 h-96 bg-[#FAF3E7] rounded-lg shadow-[0_30px_70px_-15px_rgba(0,0,0,0.5)] p-8 flex flex-col justify-between rotate-[-3deg] hover:rotate-0 transition-transform duration-500">
                {/* Timbro "PDF gratuito" centrato sulla porzione bianca del foglio */}
                <div
                  aria-hidden="true"
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-8deg] border-[3px] border-[#C05C45]/70 rounded-md px-4 py-1.5 text-[#C05C45]/80 font-ui font-bold text-[15px] uppercase tracking-widest select-none pointer-events-none"
                  style={{ fontFamily: '"Courier New", monospace' }}
                >
                  PDF gratuito
                </div>
                <div>
                  <h3 className="font-display text-2xl text-[#1A362D] leading-tight">
                    Pronti alla primaria?
                    <br />
                    <span className="text-[#C05C45]">Senza fretta.</span>
                  </h3>
                  <p className="mt-3 font-ui text-xs text-[#5C6B64] leading-relaxed">
                    10 regole d'oro per accompagnare i bambini dell'ultimo anno della scuola dell'infanzia.
                  </p>
                </div>
                <div className="border-t border-[#EFE3D2] pt-4">
                  <p className="font-ui text-[10px] text-[#8A9691] uppercase tracking-wider">
                    Per manina · Guide gratuite
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================ MECCANISMO RECENSIONE ================ */}
      <section id="come-funziona" className="py-20 bg-[#FAF3E7]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="label-eyebrow text-[#C05C45] text-center">COME SI RACCONTA</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#1A362D] text-center">
            Non è un questionario.<br className="sm:hidden" /> È una scheda veloce.
          </h2>

          {/* Anteprima visual della scheda */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-[1fr_1.1fr] gap-8 items-start">
            {/* Mock preview scheda fedele al form vero */}
            <div className="bg-white rounded-2xl border border-[#E8E6E1] shadow-[0_20px_50px_-20px_rgba(26,54,45,0.2)] p-6 max-w-md mx-auto md:mx-0">
              <div className="pb-4 border-b border-[#F2EFEB]">
                <p className="font-display text-lg text-[#1A362D] leading-tight">
                  Scuola dell'infanzia
                  <br />
                  Anna Frank
                </p>
                <p className="mt-1 font-ui text-xs text-[#8A9691]">
                  Milano · Zona 3
                </p>
              </div>

              {/* 5 dimensioni con emoji */}
              <div className="mt-4 space-y-2.5">
                {[
                  { label: "Rapporto tra le educatrici", emoji: "😊" },
                  { label: "Spazi", emoji: "🙂" },
                  { label: "Comunicazione con le famiglie", emoji: "😊" },
                  { label: "Mensa", emoji: "😊" },
                  { label: "Attività", emoji: "🤩" },
                ].map((d) => (
                  <div key={d.label} className="flex items-center justify-between">
                    <span className="font-ui text-xs text-[#5C6B64]">{d.label}</span>
                    <span className="text-lg">{d.emoji}</span>
                  </div>
                ))}
              </div>

              {/* Sceglierei di nuovo */}
              <div className="mt-4 pt-4 border-t border-[#F2EFEB]">
                <p className="font-ui text-[10px] text-[#8A9691] uppercase tracking-wider mb-2">
                  La sceglierei di nuovo?
                </p>
                <span className="inline-flex items-center gap-1.5 bg-[#DCE4DC] text-[#1A362D] rounded-full px-3 py-1 font-ui text-xs font-semibold">
                  ✓ Sì
                </span>
              </div>

              {/* Frase libera */}
              <div className="mt-4 pt-4 border-t border-[#F2EFEB]">
                <p className="font-ui text-[10px] text-[#8A9691] uppercase tracking-wider mb-2">
                  In una frase
                </p>
                <p className="font-serif italic text-sm text-[#1A362D] leading-relaxed">
                  "Mia figlia esce sempre con qualcosa da raccontare. Le educatrici la vedono davvero."
                </p>
              </div>
            </div>

            {/* Spiegazione a destra */}
            <div>
              <h3 className="font-display text-2xl text-[#1A362D]">
                Tre tocchi, due minuti.
              </h3>
              <ul className="mt-6 space-y-4">
                <li className="flex gap-3">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#C05C45] text-white font-ui font-semibold text-sm flex items-center justify-center">
                    1
                  </span>
                  <div>
                    <p className="font-ui text-[#1A362D] font-semibold">
                      Un'emoji per 5 aspetti
                    </p>
                    <p className="font-ui text-sm text-[#5C6B64]">
                      educatrici, spazi, comunicazione, mensa, attività. Puoi lasciarne vuote fino a 2.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#C05C45] text-white font-ui font-semibold text-sm flex items-center justify-center">
                    2
                  </span>
                  <div>
                    <p className="font-ui text-[#1A362D] font-semibold">
                      La sceglieresti di nuovo? (opzionale)
                    </p>
                    <p className="font-ui text-sm text-[#5C6B64]">
                      sì, no, o non lo so. Un dato prezioso per chi ancora deve scegliere.
                    </p>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="shrink-0 w-8 h-8 rounded-full bg-[#C05C45] text-white font-ui font-semibold text-sm flex items-center justify-center">
                    3
                  </span>
                  <div>
                    <p className="font-ui text-[#1A362D] font-semibold">
                      Una frase in libertà
                    </p>
                    <p className="font-ui text-sm text-[#5C6B64]">
                      quella che riassume la tua esperienza. Sincera, non serve fare la giornalista.
                    </p>
                  </div>
                </li>
              </ul>
              <p className="mt-6 font-ui text-sm text-[#5C6B64] italic">
                E appena pubblichi, sulla stessa schermata compare il bottone{" "}
                <strong className="not-italic text-[#C05C45]">"Scarica la guida"</strong>. Fatto.
              </p>
            </div>
          </div>

          {/* Search inline */}
          <div className="mt-14 max-w-3xl mx-auto">
            <p className="text-center font-ui text-sm text-[#5C6B64] mb-3">
              Cerca la scuola di tuo figlio e apri la sua scheda.
            </p>
            <form
              onSubmit={submitSearch}
              className="bg-white rounded-2xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] p-3 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-2"
              data-testid="guida-search-form"
            >
              <div className="flex-1 flex items-center bg-[#FAF9F6] border border-[#E8E6E1] rounded-xl px-4 py-3">
                <Search size={20} className="text-[#8A9691] mr-2 shrink-0" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Nome della scuola, comune o CAP…"
                  data-testid="guida-search-input"
                  className="flex-1 bg-transparent border-0 outline-none font-ui text-base text-[#1A362D] placeholder:text-[#8A9691]"
                  autoComplete="off"
                />
              </div>
              <button
                type="submit"
                data-testid="guida-search-submit"
                className="btn-primary text-base whitespace-nowrap"
              >
                Cerca la scuola →
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ================ CHI L'HA SCRITTA ================ */}
      <section className="py-20 bg-[#F4F1EA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="label-eyebrow text-[#C05C45] text-center">CHI L'HA SCRITTA</p>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl text-[#1A362D] text-center">
            Trentacinque anni accanto ai bambini.
          </h2>

          <div className="mt-12 bg-white rounded-2xl border border-[#E8E6E1] p-8 flex flex-col sm:flex-row gap-8 items-center sm:items-start">
            {/* Foto autrice */}
            {EDUCATRICE.photoUrl ? (
              <img
                src={EDUCATRICE.photoUrl}
                alt={`Foto di ${EDUCATRICE.nome}`}
                data-testid="educatrice-photo"
                className="shrink-0 w-32 h-32 sm:w-40 sm:h-40 rounded-full object-cover border border-[#E8E6E1]"
              />
            ) : (
              <div className="shrink-0 w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-gradient-to-br from-[#DCE4DC] to-[#C0D0BE] flex items-center justify-center">
                <span className="font-display text-5xl text-[#1A362D] opacity-40">
                  {EDUCATRICE.nome.split(" ").map((n) => n[0]).join("")}
                </span>
              </div>
            )}

            <div className="flex-1 text-center sm:text-left">
              <h3 className="font-display text-2xl text-[#1A362D]">
                {EDUCATRICE.nome}
              </h3>
              <p className="mt-1 font-ui text-sm text-[#C05C45] font-semibold">
                {EDUCATRICE.qualifica}
              </p>
              <p className="mt-4 font-ui text-[#5C6B64] leading-relaxed">
                {EDUCATRICE.bioParts.map((p, i) =>
                  p.bold ? (
                    <strong key={i} className="text-[#1A362D]">{p.t}</strong>
                  ) : (
                    <span key={i}>{p.t}</span>
                  )
                )}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================ CTA FINALE ================ */}
      <section className="py-20 bg-[#1A362D]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Clock size={32} className="mx-auto text-[#F2C94C]" strokeWidth={1.5} />
          <h2 className="mt-6 font-display text-3xl sm:text-4xl text-white leading-tight">
            Cinque minuti oggi.
            <br />
            <span className="text-[#F2C94C]">Un anno più sereno.</span>
          </h2>
          <p className="mt-6 font-ui text-white/80 max-w-xl mx-auto">
            Racconta la tua esperienza, ricevi la guida, aiuta un altro genitore
            che sta per fare quello che hai già fatto tu.
          </p>
          <a
            href="#come-funziona"
            data-testid="guida-cta-finale"
            className="inline-block mt-8 btn-primary text-base px-8 py-3"
          >
            Inizia ora →
          </a>
        </div>
      </section>
    </div>
  );
}
